/**
 * "Case No. & Court" on a legal file (Elle Baylon, Ref 9bb64edd60, 28 Oct 2026) — the free-text reference the team kept
 * on Airtable ("1157 of 2026 / N76CL174 / County Court at Central London"). Shown under the customer on the tracker and in
 * the case file; click to edit. Searchable from the tracker search box and the Court filter.
 */
import { useEffect, useState } from "react";
import { Landmark, Loader2, Pencil } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { setLegalCourtReference } from "@/lib/api";

export function CourtReferenceInline({ caseRow, onSaved, variant = "row" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(caseRow.court_reference || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(caseRow.court_reference || ""); }, [caseRow.court_reference]);

  const current = caseRow.court_reference || "";
  const fallback = !current && caseRow.court_case_number ? `${caseRow.court_case_number}${caseRow.chosen_court?.name ? ` / ${caseRow.chosen_court.name}` : ""}` : "";

  const save = async () => {
    if (val.trim() === current.trim()) { setOpen(false); return; }
    setSaving(true);
    try {
      const r = await setLegalCourtReference(caseRow.case_id, val, user?.name || user?.email);
      caseRow.court_reference = r.court_reference; caseRow.court_name = r.court_name;
      toast.success(r.court_reference ? "Case no. & court saved" : "Case no. & court cleared");
      setOpen(false);
      onSaved?.(r);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const text = current || fallback;
  const field = (
    <div className="space-y-1.5 w-[360px] max-w-[90vw]">
      <p className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-violet-600" /> Case no. & court</p>
      <Input value={val} onChange={(e) => setVal(e.target.value)} autoFocus disabled={saving}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setOpen(false); }}
        placeholder="e.g. 1157 of 2026 / N76CL174 / County Court at Central London" className="h-8 text-xs" data-testid="legal-court-ref-input" />
      <p className="text-[10px] text-slate-500">Petition / claim numbers and the court, separated by “/”. The court name is picked out for the Court filter.</p>
      <div className="flex justify-end gap-1.5">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(false)} disabled={saving} data-testid="legal-court-ref-cancel">Cancel</Button>
        <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving} data-testid="legal-court-ref-save">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}</Button>
      </div>
    </div>
  );

  if (variant === "field") {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="w-full flex items-center gap-2 border rounded px-2 py-1 bg-violet-50/40 hover:bg-violet-50 text-left" data-testid="legal-court-reference">
            <Landmark className="w-3.5 h-3.5 text-violet-600 shrink-0" />
            <span className="text-[11px] text-slate-600 shrink-0">Case no. & court</span>
            <span className={`text-[11px] flex-1 truncate ${text ? "text-slate-900 font-medium" : "text-slate-400 italic"}`} data-testid="legal-court-reference-value">{text || "not recorded — click to add"}</span>
            <Pencil className="w-3 h-3 text-slate-400 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-3" onClick={(e) => e.stopPropagation()}>{field}</PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={`group/cr mt-0.5 inline-flex items-center gap-1 max-w-full text-[10px] rounded px-1 -mx-1 hover:bg-violet-50 ${text ? "text-violet-800" : "text-slate-400 opacity-0 group-hover:opacity-100"}`}
          title={text ? `${text} — click to edit` : "Add case no. & court"}
          data-testid={`legal-court-ref-${caseRow.case_id}`}
        >
          <Landmark className="w-2.5 h-2.5 shrink-0" />
          <span className="truncate max-w-[260px]">{text || "Add case no. & court"}</span>
          <Pencil className="w-2.5 h-2.5 shrink-0 opacity-0 group-hover/cr:opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-3" onClick={(e) => e.stopPropagation()}>{field}</PopoverContent>
    </Popover>
  );
}
